/* ============================================================
   LUXURY RECOVERY USA — Theme JavaScript
   Handles: header scroll, mobile menu, cart drawer, qty controls,
            specs accordion, scroll reveal, newsletter
   ============================================================ */

const LRU = (() => {

  /* — Header scroll behavior ————————————————————— */
  const initHeader = () => {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const onScroll = () => {
      header.classList.toggle('scrolled', window.scrollY > 20);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  /* — Cart Drawer ————————————————————————————————— */
  const openCart = () => {
    document.getElementById('cart-drawer')?.classList.add('open');
    document.getElementById('cart-overlay')?.classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  const closeCart = () => {
    document.getElementById('cart-drawer')?.classList.remove('open');
    document.getElementById('cart-overlay')?.classList.remove('open');
    document.body.style.overflow = '';
  };

  const initCart = () => {
    document.querySelectorAll('[data-open-cart]').forEach(el => {
      el.addEventListener('click', openCart);
    });
    document.getElementById('cart-drawer-close')?.addEventListener('click', closeCart);
  };

  /* — Quantity controls ———————————————————————————— */
  const initQtyControls = () => {
    document.querySelectorAll('.qty-control, .qty-control-lg').forEach(ctrl => {
      const minus = ctrl.querySelector('[data-qty-minus]');
      const plus  = ctrl.querySelector('[data-qty-plus]');
      const val   = ctrl.querySelector('.qty-value, .qty-value-lg');
      if (!minus || !plus || !val) return;

      minus.addEventListener('click', () => {
        const current = parseInt(val.textContent) || 1;
        if (current > 1) val.textContent = current - 1;
      });
      plus.addEventListener('click', () => {
        const current = parseInt(val.textContent) || 1;
        val.textContent = current + 1;
      });
    });
  };

  /* — Specs accordion ——————————————————————————————— */
  const initAccordions = () => {
    document.querySelectorAll('.specs-toggle').forEach(toggle => {
      toggle.addEventListener('click', () => {
        const isOpen = toggle.classList.toggle('open');
        const grid = toggle.nextElementSibling;
        if (grid) grid.classList.toggle('open', isOpen);
      });
    });
  };

  /* — Scroll reveal ————————————————————————————————— */
  const initReveal = () => {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
  };

  /* — Newsletter form ——————————————————————————————— */
  const initNewsletter = () => {
    document.querySelectorAll('[data-newsletter-form]').forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = form.querySelector('input[type="email"]');
        const btn   = form.querySelector('button[type="submit"]');
        if (!input || !btn) return;
        btn.textContent = 'Subscribed!';
        btn.disabled = true;
        input.value = '';
        setTimeout(() => {
          btn.textContent = 'Subscribe';
          btn.disabled = false;
        }, 3000);
      });
    });
  };

  /* — Add to cart (Shopify AJAX) ———————————————————— */
  const addToCart = async (variantId, quantity = 1) => {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await refreshCart();
      openCart();
    } catch (err) {
      console.error(err);
    }
  };

  /* — Refresh cart drawer from Shopify —————————————— */
  const refreshCart = async () => {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      updateCartCount(cart.item_count);
      updateCartDrawer(cart);
    } catch (err) {
      console.error(err);
    }
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
    const drawerCount = document.querySelector('.cart-drawer__count');
    if (drawerCount) drawerCount.textContent = `(${count} item${count !== 1 ? 's' : ''})`;
  };

  const formatMoney = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const updateCartDrawer = (cart) => {
    const itemsEl = document.getElementById('cart-drawer-items');
    const footerEl = document.getElementById('cart-drawer-footer');
    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = `
        <div class="cart-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          <p class="cart-empty__title">Your cart is empty</p>
          <p class="cart-empty__sub">Add some products to get started</p>
          <button class="cart-empty__btn" onclick="LRU.closeCart()">Continue Shopping</button>
        </div>`;
      if (footerEl) footerEl.style.display = 'none';
      return;
    }

    if (footerEl) footerEl.style.display = 'block';

    itemsEl.innerHTML = cart.items.map(item => `
      <div class="cart-item">
        <div class="cart-item__img">
          <img src="${item.image}" alt="${item.product_title}" loading="lazy">
        </div>
        <div class="cart-item__info">
          <p class="cart-item__vendor">${item.vendor}</p>
          <p class="cart-item__name">${item.product_title}</p>
          <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
          <div class="cart-item__actions">
            <div class="qty-control">
              <button class="qty-btn" onclick="LRU.updateCartItem('${item.key}', ${item.quantity - 1})">−</button>
              <span class="qty-value">${item.quantity}</span>
              <button class="qty-btn" onclick="LRU.updateCartItem('${item.key}', ${item.quantity + 1})">+</button>
            </div>
            <button class="cart-item__remove" onclick="LRU.updateCartItem('${item.key}', 0)">Remove</button>
          </div>
        </div>
      </div>`).join('');

    const subtotalEl = document.getElementById('cart-subtotal');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
  };

  const updateCartItem = async (key, quantity) => {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      await refreshCart();
    } catch (err) {
      console.error(err);
    }
  };

  /* — Init ——————————————————————————————————————— */
  const init = () => {
    initHeader();
    initCart();
    initQtyControls();
    initAccordions();
    initReveal();
    initNewsletter();
    refreshCart();
  };

  document.addEventListener('DOMContentLoaded', init);

  return { openCart, closeCart, addToCart, updateCartItem, refreshCart };
})();

window.LRU = LRU;